import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { Toast } from '../../components/base/Toast';
import { api } from '../../services/api';
import { formatPrice } from '../../utils/format';
import { FloatingActionButton } from '../../components/base/FloatingActionButton';
import { checkPermission, PERMISSIONS } from '../../utils/permissions';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useRef } from 'react';

interface Supplier {
  id: number;
  nom: string;
  telephone: string;
  adresse: string;
  total_du: number;
  total_paye: number;
  solde: number;
  last_appro?: string;
  created_at: string;
}

interface Lot {
  id: number;
  fournisseur_id: number;
  date_achat: string;
  montant_total: number;
  montant_paye: number;
  statut_paiement: 'unpaid' | 'partial' | 'paid';
  notes: string;
  created_at: string;
}

interface Article {
  id: number;
  lot_id: number;
  designation: string;
  quantite: number;
  unite: string;
  prix_unitaire: number;
  total: number;
}

interface Payment {
  id: number;
  lot_id: number;
  montant: number;
  date_paiement: string;
  mode_paiement: string;
  commentaire: string;
}

type DetailTab = 'lots' | 'articles' | 'paiements';

export default function FournisseurPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  // View state
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [activeTab, setActiveTab] = useState<DetailTab>('lots');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [supplierLots, setSupplierLots] = useState<Lot[]>([]);
  const [selectedLotArticles, setSelectedLotArticles] = useState<Article[]>([]);
  const [selectedLotPayments, setSelectedLotPayments] = useState<Payment[]>([]);

  // Modals
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [showAddLot, setShowAddLot] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState<Lot | null>(null);
  const [showLotArticles, setShowLotArticles] = useState<Lot | null>(null);

  // Form states
  const [newSupplier, setNewSupplier] = useState({ nom: '', telephone: '', adresse: '' });
  const [newLot, setNewLot] = useState({ 
    date_achat: new Date().toISOString().split('T')[0], 
    montant_total: 0, 
    notes: '',
    nom_tissu: '',
    quantite: 0,
    prix_unitaire: 0,
    articles: [] as any[] 
  });
  const [paymentModes, setPaymentModes] = useState<any[]>([]);
  const [editingLot, setEditingLot] = useState<any>(null);
  const [showAddArticle, setShowAddArticle] = useState(false);
  const [newArticle, setNewArticle] = useState({ designation: '', quantite: 0, prix_unitaire: 0, seuil_alerte: 5 });
  const [newPayment, setNewPayment] = useState({ montant: 0, mode_paiement: 'Espèces', commentaire: '' });
  const [showManagePaymentModes, setShowManagePaymentModes] = useState(false);
  const [newPaymentModeLabel, setNewPaymentModeLabel] = useState('');

  useEffect(() => {
    const stored = localStorage.getItem('atelier_bassoum_user');
    const token = localStorage.getItem('atelier_bassoum_token');
    if (stored && token) {
      const u = JSON.parse(stored);
      setUser(u);
      
      // Sécurité : Redirection si pas de permission
      if (u.role !== 'superadmin' && u.role !== 'gerant' && !checkPermission(PERMISSIONS.MANAGE_SUPPLIERS)) {
          navigate('/dashboard');
          return;
      }
      
      fetchSuppliers(token);
      fetchPaymentModes(token);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const fetchPaymentModes = async (token: string) => {
    try {
      const data = await api.getPaymentModes(token);
      setPaymentModes(data);
    } catch (err) {
      console.error("Erreur modes paiement", err);
    }
  };

  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceLot, setInvoiceLot] = useState<any>(null);
  const [invoiceMode, setInvoiceMode] = useState<'lot' | 'global'>('lot');

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR') + ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const handleAddArticle = async () => {
    if (!showLotArticles || !newArticle.designation || !newArticle.quantite) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.addLotArticle(token, showLotArticles.id, newArticle);
      showToast("Article ajouté au lot");
      setShowAddArticle(false);
      setNewArticle({ designation: '', quantite: 0, prix_unitaire: 0, seuil_alerte: 5 });
      
      // Rafraîchir les détails du lot ET la liste des lots pour mettre à jour les montants globaux
      const details = await api.getLotDetails(token, showLotArticles.id);
      setSelectedLotArticles(details.articles || []);
      if (selectedSupplier) handleSelectSupplier(selectedSupplier); 
    } catch (err) {
      showToast("Erreur lors de l'ajout", "error");
    }
  };

  const handleUpdateStock = async (articleId: number, decrement: number) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token || !showLotArticles) return;
    try {
      await api.updateArticleStock(token, articleId, decrement);
      showToast("Stock mis à jour");
      const details = await api.getLotDetails(token, showLotArticles.id);
      setSelectedLotArticles(details.articles || []);
    } catch (err) {
      showToast("Erreur stock", "error");
    }
  };

  const handleDeleteLot = async (lotId: number) => {
    if (!selectedSupplier || !window.confirm("Supprimer ce lot ainsi que TOUS ses articles et paiements ? Cette action est irréversible.")) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.deleteLot(token, selectedSupplier.id, lotId);
      showToast("Lot supprimé");
      handleSelectSupplier(selectedSupplier);
    } catch (err) {
      showToast("Erreur suppression lot", "error");
    }
  };

  const handleUpdateLot = async () => {
    if (!editingLot || !selectedSupplier) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.updateLot(token, editingLot.id, editingLot);
      showToast("Lot mis à jour");
      setEditingLot(null);
      handleSelectSupplier(selectedSupplier);
    } catch (err) {
      showToast("Erreur modification lot", "error");
    }
  };

  const fetchSuppliers = async (token: string) => {
    try {
      const data = await api.getFournisseurs(token);
      setSuppliers(data);
    } catch (err) {
      showToast("Erreur lors de la récupération des fournisseurs", "error");
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleCreatePaymentMode = async () => {
    if (!newPaymentModeLabel) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.createPaymentMode(token, { nom: newPaymentModeLabel });
      showToast("Mode de paiement ajouté");
      setNewPaymentModeLabel('');
      fetchPaymentModes(token);
    } catch (err) {
      showToast("Erreur lors de l'ajout", "error");
    }
  };

  const handleDeletePaymentMode = async (id: number) => {
    if (!window.confirm("Supprimer ce mode de paiement ?")) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.deletePaymentMode(token, id);
      showToast("Mode de paiement supprimé");
      fetchPaymentModes(token);
    } catch (err) {
      showToast("Erreur lors de la suppression", "error");
    }
  };

  const handleSelectSupplier = async (s: Supplier) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      setLoading(true);
      const lots = await api.getFournisseurLots(token, s.id);
      
      // On récupère le fournisseur le plus à jour pour les totaux
      const updatedList = await api.getFournisseurs(token);
      setSuppliers(updatedList);
      const updatedS = updatedList.find((sup: Supplier) => sup.id === s.id) || s;
      
      setSelectedSupplier(updatedS);
      setSupplierLots(lots);
      setSelectedLotPayments([]); 
      setView('detail');
      setActiveTab('lots');
    } catch (err) {
      showToast("Erreur lors de la récupération des détails", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSupplier = async () => {
    if (!newSupplier.nom) return showToast("Le nom est requis", "error");
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.createFournisseur(token, newSupplier);
      showToast("Fournisseur ajouté avec succès");
      setShowAddSupplier(false);
      setNewSupplier({ nom: '', telephone: '', adresse: '' });
      fetchSuppliers(token);
    } catch (err) {
      showToast("Erreur lors de l'ajout", "error");
    }
  };

  const handleAddLot = async () => {
    if (!selectedSupplier || newLot.montant_total <= 0) return showToast("Montant invalide", "error");
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.createFournisseurLot(token, {
        fournisseur_id: selectedSupplier.id,
        ...newLot
      });
      showToast("Lot ajouté avec succès");
      setShowAddLot(false);
      setNewLot({ 
        date_achat: new Date().toISOString().split('T')[0], 
        montant_total: 0, 
        notes: '', 
        nom_tissu: '', 
        quantite: 0, 
        prix_unitaire: 0, 
        articles: [] as any[] 
      });
      handleSelectSupplier(selectedSupplier);
    } catch (err) {
      showToast("Erreur lors de l'ajout du lot", "error");
    }
  };

  const handleAddPayment = async () => {
    if (!showAddPayment || newPayment.montant <= 0) return showToast("Montant invalide", "error");
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.addLotPaiement(token, showAddPayment.id, newPayment);
      showToast("Paiement enregistré");
      setShowAddPayment(null);
      setNewPayment({ montant: 0, mode_paiement: 'Espèces', commentaire: '' });
      if (selectedSupplier) handleSelectSupplier(selectedSupplier);
    } catch (err) {
      showToast("Erreur lors du paiement", "error");
    }
  };

  const handleViewArticles = async (lot: Lot) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
        const details = await api.getLotDetails(token, lot.id);
        setSelectedLotArticles(details.articles || []);
        setShowLotArticles(lot);
    } catch (err) {
        showToast("Erreur chargement articles", "error");
    }
  };

  const handleViewPayments = async () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token || !selectedSupplier) return;
    try {
        setLoading(true);
        const allPayments: Payment[] = [];
        for (const lot of supplierLots) {
            const details = await api.getLotDetails(token, lot.id);
            if (details.paiements) {
                allPayments.push(...details.paiements.map((p: any) => ({...p, lot_id: lot.id})));
            }
        }
        allPayments.sort((a, b) => new Date(b.date_paiement).getTime() - new Date(a.date_paiement).getTime());
        setSelectedLotPayments(allPayments);
        setActiveTab('paiements');
    } catch (err) {
        showToast("Erreur chargement paiements", "error");
    } finally {
        setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (!detailRef.current || !selectedSupplier) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(detailRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Bilan_${selectedSupplier.nom}_${new Date().toISOString().split('T')[0]}.pdf`);
      showToast("PDF généré");
    } catch (err) {
      showToast("Erreur export PDF", "error");
    } finally {
      setExporting(false);
    }
  };

  const handleExportImage = async () => {
    if (!detailRef.current || !selectedSupplier) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(detailRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      link.download = `Bilan_${selectedSupplier.nom}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      showToast("Image téléchargée");
    } catch (err) {
      showToast("Erreur export Image", "error");
    } finally {
      setExporting(false);
    }
  };

  const handleExportWhatsApp = () => {
    if (!selectedSupplier) return;
    const reste = selectedSupplier.total_du - selectedSupplier.total_paye;
    const text = `*BILAN FOURNISSEUR - ATELIER BASSOUM*\n\n` +
                 `Fournisseur : ${selectedSupplier.nom}\n` +
                 `Total Achats : ${formatPrice(selectedSupplier.total_du)}\n` +
                 `Total Payé : ${formatPrice(selectedSupplier.total_paye)}\n` +
                 `*Reste à payer : ${formatPrice(reste)}*\n\n` +
                 `Merci pour votre confiance.`;
    
    window.open(`https://wa.me/${selectedSupplier.telephone.replace(/\s+/g, '')}?text=${encodeURIComponent(text)}`, '_blank');
  };

  const isLate = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    return diff > (30 * 24 * 60 * 60 * 1000); // 30 jours
  };

  const filteredSuppliers = suppliers.filter(s => 
    s.nom.toLowerCase().includes(searchTerm.toLowerCase()) || 
    s.telephone.includes(searchTerm)
  );

  if (!user) return null;

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      <div className="hidden md:block">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            {view === 'detail' && (
              <button 
                onClick={() => setView('list')}
                title="Retour à la liste"
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-50 text-gray-400 hover:text-gray-900 cursor-pointer transition-all border border-gray-100"
              >
                <i className="ri-arrow-left-line text-xl" />
              </button>
            )}
            <div>
              <h2 className="font-playfair font-bold text-xl text-gray-900 tracking-tight">
                {view === 'list' ? 'Gestion des Fournisseurs' : selectedSupplier?.nom}
              </h2>
              <p className="text-[11px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">
                {view === 'list' ? `RÉSEAU DE ${suppliers.length} PARTENAIRES` : `SUIVI DÉTAILLÉ DU COMPTE`}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {view === 'list' ? (
              <button 
                onClick={() => setShowAddSupplier(true)}
                className="hidden md:flex bg-foret-600 hover:bg-foret-700 text-white px-5 py-2.5 rounded-2xl text-sm font-black items-center gap-2 shadow-xl shadow-foret-100 transition-all cursor-pointer active:scale-95"
              >
                <i className="ri-user-add-line text-lg" />
                NOUVEAU FOURNISSEUR
              </button>
            ) : (
              <>
                <button 
                  onClick={handleExportPDF}
                  disabled={exporting}
                  className="bg-white border-2 border-gray-100 text-gray-600 px-3 sm:px-5 py-2.5 rounded-2xl text-sm font-black flex items-center gap-2 hover:bg-gray-50 transition-all cursor-pointer disabled:opacity-50"
                  title="Exporter en PDF"
                >
                  <i className="ri-file-pdf-line text-lg" />
                  <span className="hidden sm:inline">PDF</span>
                </button>
                <button 
                  onClick={handleExportImage}
                  disabled={exporting}
                  className="bg-white border-2 border-gray-100 text-gray-600 px-3 sm:px-5 py-2.5 rounded-2xl text-sm font-black flex items-center gap-2 hover:bg-gray-50 transition-all cursor-pointer disabled:opacity-50"
                  title="Exporter en Image"
                >
                  <i className="ri-image-line text-lg" />
                  <span className="hidden sm:inline">IMAGE</span>
                </button>
                <button 
                  onClick={() => setShowAddLot(true)}
                  className="hidden md:flex bg-foret-600 hover:bg-foret-700 text-white px-5 py-2.5 rounded-2xl text-sm font-black items-center gap-2 shadow-xl shadow-foret-100 transition-all cursor-pointer active:scale-95"
                >
                  <i className="ri-add-box-line text-lg" />
                  NOUVEL ARRIVAGE
                </button>
              </>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-8 lg:p-12">
          {view === 'list' ? (
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
                <div className="lg:col-span-2 relative drop-shadow-sm">
                  <i className="ri-search-line absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                  <input 
                    type="text"
                    title="Rechercher un fournisseur"
                    placeholder="Chercher un partenaire (Nom, Tel, Ville)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-white border-2 border-transparent rounded-[1.5rem] py-5 pl-14 pr-6 text-sm font-medium focus:outline-none focus:border-foret-500 shadow-sm transition-all"
                  />
                </div>
                <div className="bg-gray-900 rounded-[1.5rem] p-5 flex items-center gap-5 shadow-2xl shadow-gray-200">
                  <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center border border-white/5">
                    <i className="ri-money-dollar-circle-line text-foret-400 text-3xl" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-[2px]">Encours Global</p>
                    <p className="text-2xl font-black text-white">
                      {formatPrice(suppliers.reduce((acc, s) => acc + (s.total_du - s.total_paye), 0))}
                    </p>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="flex flex-col items-center justify-center py-32">
                  <div className="w-12 h-12 border-4 border-foret-100 border-t-foret-600 rounded-full animate-spin mb-6" />
                  <p className="text-gray-400 text-sm font-bold tracking-widest uppercase">Initialisation des données...</p>
                </div>
              ) : filteredSuppliers.length === 0 ? (
                <div className="bg-white border-2 border-dashed border-gray-200 rounded-[3rem] py-24 text-center">
                  <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-8">
                    <i className="ri-truck-line text-5xl text-gray-200" />
                  </div>
                  <h3 className="text-2xl font-playfair font-black text-gray-900 mb-3">Aucun fournisseur enregistré</h3>
                  <p className="text-gray-400 text-sm max-w-sm mx-auto mb-10 leading-relaxed font-medium">
                    Centralisez vos achats de tissus et accessoires. Suivez vos dettes et vos paiements au même endroit.
                  </p>
                  <button 
                    onClick={() => setShowAddSupplier(true)}
                    className="bg-gray-900 text-white px-10 py-4 rounded-2xl font-black text-sm hover:bg-black transition-all cursor-pointer shadow-2xl shadow-gray-300 active:scale-95"
                  >
                    AJOUTER MON PREMIER PARTENAIRE
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredSuppliers.map(s => {
                    const reste = s.total_du - s.total_paye;
                    return (
                        <div 
                        key={s.id}
                        onClick={() => handleSelectSupplier(s)}
                        className="group bg-white border-2 border-gray-50 rounded-[2.5rem] p-8 hover:shadow-2xl hover:border-foret-100 transition-all cursor-pointer relative overflow-hidden flex flex-col"
                        >
                        <div className="flex items-start justify-between mb-6">
                            <div className="flex items-center gap-5">
                            <div className="w-16 h-16 bg-gray-50 rounded-[1.25rem] flex items-center justify-center group-hover:bg-foret-50 transition-all border border-gray-100">
                                <i className="ri-building-2-line text-3xl text-gray-300 group-hover:text-foret-600" />
                            </div>
                            <div>
                                <p className="font-black text-xl text-gray-900 group-hover:text-foret-600 transition-colors uppercase leading-none">{s.nom}</p>
                                <div className="flex items-center gap-2 text-gray-400 text-[11px] font-black mt-2 tracking-wide">
                                <i className="ri-phone-fill text-foret-500" />
                                <span>{s.telephone || '—'}</span>
                                </div>
                            </div>
                            </div>
                            { reste > 0 && (
                            <div className="w-3.5 h-3.5 bg-red-500 rounded-full animate-pulse shadow-lg shadow-red-200 ring-4 ring-red-50" />
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-4 mt-auto">
                            <div className="bg-gray-50/50 rounded-2xl p-4 border border-gray-50 group-hover:bg-white transition-all">
                            <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">Engagé</p>
                            <p className="font-black text-gray-900">{formatPrice(s.total_du)}</p>
                            </div>
                            <div className="bg-red-50/50 rounded-2xl p-4 border border-red-50 group-hover:bg-white transition-all">
                            <p className="text-[10px] text-red-400 font-black uppercase tracking-widest mb-1">À Régler</p>
                            <p className="font-black text-red-600">{formatPrice(reste)}</p>
                            </div>
                        </div>

                        <div className="mt-8">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Progression des paiements</span>
                                <span className="text-[10px] font-black text-foret-600">{s.total_du > 0 ? Math.round((s.total_paye / s.total_du) * 100) : 0}%</span>
                            </div>
                            <div className="w-full bg-gray-100 h-2.5 rounded-full overflow-hidden p-0.5 border border-gray-50">
                                <style>{`
                                .fourn-prog-bar-${s.id} {
                                    width: ${s.total_du > 0 ? (s.total_paye / s.total_du) * 100 : 0}%;
                                }
                                `}</style>
                                <div className={`bg-foret-500 h-full rounded-full transition-all duration-1000 shadow-sm fourn-prog-bar-${s.id}`} />
                            </div>
                        </div>
                        
                        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-foret-500 transition-transform translate-y-full group-hover:translate-y-0" />
                        </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="max-w-6xl mx-auto pb-20" ref={detailRef}>
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-12">
                    <div className="bg-white border-2 border-gray-50 rounded-[2rem] p-7 shadow-sm">
                        <div className="flex items-center gap-5 mb-5">
                            <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center border border-gray-100">
                                <i className="ri-file-list-3-line text-2xl text-gray-400" />
                            </div>
                            <div>
                                <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Total Lots</p>
                                <p className="text-2xl font-black text-gray-900">{supplierLots.length}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white border-2 border-gray-50 rounded-[2rem] p-7 shadow-sm">
                        <div className="flex items-center gap-5 mb-5">
                            <div className="w-14 h-14 bg-foret-50 rounded-2xl flex items-center justify-center border border-foret-100">
                                <i className="ri-hand-coin-line text-2xl text-foret-600" />
                            </div>
                            <div>
                                <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Cumul Payé</p>
                                <p className="text-2xl font-black text-foret-600">{formatPrice(selectedSupplier?.total_paye || 0)}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white border-2 border-red-50 rounded-[2rem] p-7 shadow-sm">
                        <div className="flex items-center gap-5 mb-5">
                            <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center border border-red-100">
                                <i className="ri-error-warning-line text-2xl text-red-500" />
                            </div>
                            <div>
                                <p className="text-[10px] text-red-400 font-black uppercase tracking-widest">Reste Dû</p>
                                <p className="text-2xl font-black text-red-600">{formatPrice((selectedSupplier?.total_du || 0) - (selectedSupplier?.total_paye || 0))}</p>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-3">
                        <button 
                            onClick={handleExportWhatsApp}
                            className="bg-green-600 hover:bg-green-700 text-white rounded-2xl py-4 text-xs font-black flex items-center justify-center gap-2 transition-all cursor-pointer shadow-xl shadow-green-100 active:scale-95"
                        >
                            <i className="ri-whatsapp-line text-xl" />
                            ENVOYER BILAN WHATSAPP
                        </button>
                        <button 
                            title="Envoyer le bilan par email"
                            className="bg-gray-900 border border-white/10 hover:bg-black text-white rounded-2xl py-2 text-[10px] font-black flex items-center justify-center gap-2 transition-all cursor-pointer active:scale-95"
                        >
                            <i className="ri-mail-line" />
                            ENVOYER PAR EMAIL
                        </button>
                    </div>
                </div>

                <div className="bg-white border-2 border-gray-50 rounded-[2.5rem] shadow-sm relative overflow-hidden min-h-[500px] flex flex-col">
                    <div className="flex items-center gap-10 px-10 pt-8 border-b border-gray-50">
                        <button 
                            onClick={() => setActiveTab('lots')}
                            className={`text-xs font-black pb-5 tracking-widest transition-all cursor-pointer border-b-4 ${activeTab === 'lots' ? 'text-foret-600 border-foret-500' : 'text-gray-300 border-transparent hover:text-gray-500'}`}
                        >
                            HISTORIQUE DES LOTS
                        </button>
                        <button 
                            onClick={handleViewPayments}
                            className={`text-xs font-black pb-5 tracking-widest transition-all cursor-pointer border-b-4 ${activeTab === 'paiements' ? 'text-foret-600 border-foret-500' : 'text-gray-300 border-transparent hover:text-gray-500'}`}
                        >
                            HISTORIQUE DES PAIEMENTS
                        </button>
                    </div>

                    <div className="p-10 flex-1">
                        {activeTab === 'lots' && (
                            supplierLots.length === 0 ? (
                                <div className="py-24 text-center opacity-40">
                                    <i className="ri-archive-line text-6xl mb-4 block" />
                                    <p className="font-bold uppercase tracking-widest">Aucun lot à afficher</p>
                                </div>
                            ) : (
                                <div className="grid gap-6">
                                    {supplierLots.map(lot => {
                                        const reste = lot.montant_total - lot.montant_paye;
                                        const late = reste > 0 && isLate(lot.date_achat);
                                        return (
                                            <div key={lot.id} className="flex flex-col lg:flex-row items-center justify-between p-8 rounded-[2rem] bg-gray-50/50 border-2 border-transparent hover:border-foret-100 hover:bg-white transition-all group relative overflow-hidden">
                                                {late && <div className="absolute top-0 right-0 px-4 py-1 bg-red-600 text-[10px] text-white font-black rounded-bl-xl tracking-tighter animate-pulse">RETARD RÉGLEMENT (+30j)</div>}
                                                <div className="flex items-center gap-8 w-full lg:w-auto">
                                                    <div className={`w-16 h-16 rounded-2xl flex flex-col items-center justify-center shrink-0 shadow-sm ${reste > 0 ? 'bg-red-50 text-red-500 border border-red-100' : 'bg-foret-50 text-foret-600 border border-foret-100'}`}>
                                                        <span className="text-[10px] font-black uppercase tracking-tighter">Lot</span>
                                                        <span className="text-xl font-black leading-tight">#{lot.id}</span>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-black text-gray-900 group-hover:text-foret-600 transition-colors uppercase mb-1">
                                                            {new Date(lot.date_achat).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                                        </p>
                                                        <div className="flex items-center gap-4">
                                                            <span className="text-xs font-bold text-gray-400 flex items-center gap-1.5 bg-white px-3 py-1 rounded-full border border-gray-100 shadow-sm">
                                                                <i className="ri-price-tag-3-fill text-gray-300" />
                                                                {formatPrice(lot.montant_total)}
                                                            </span>
                                                            <span className={`text-[10px] font-black px-4 py-1.5 rounded-full border ${reste === 0 ? 'bg-foret-500 text-white border-foret-500' : 'bg-white text-red-600 border-red-200'}`}>
                                                                {reste === 0 ? 'INTÉGRALEMENT SOLDÉ' : `${formatPrice(reste)} À RÉGLER`}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-3 w-full lg:w-auto mt-6 lg:mt-0">
                                                    <button 
                                                        onClick={async () => { 
                                                            const token = localStorage.getItem('atelier_bassoum_token');
                                                            if (token) {
                                                                const details = await api.getLotDetails(token, lot.id);
                                                                setSelectedLotArticles(details.articles || []);
                                                                setSelectedLotPayments(details.paiements || []);
                                                            }
                                                            setInvoiceLot(lot); 
                                                            setInvoiceMode('lot'); 
                                                            setShowInvoice(true); 
                                                        }}
                                                        className="flex-1 lg:flex-none px-6 py-3.5 bg-white border-2 border-gray-100 text-gray-900 rounded-2xl text-[11px] font-black hover:bg-gray-50 transition-all cursor-pointer active:scale-95 whitespace-nowrap"
                                                    >
                                                        FACTURE
                                                    </button>
                                                    {reste > 0 && (
                                                        <button 
                                                            onClick={() => setShowAddPayment(lot)}
                                                            className="flex-1 lg:flex-none px-8 py-3.5 bg-gray-900 text-white rounded-2xl text-[11px] font-black hover:bg-black transition-all cursor-pointer shadow-xl active:scale-95 whitespace-nowrap"
                                                        >
                                                            RÉGLER
                                                        </button>
                                                    )}
                                                    <button 
                                                        onClick={() => handleViewArticles(lot)}
                                                        className="w-14 h-14 bg-white border-2 border-gray-100 rounded-2xl flex items-center justify-center text-gray-400 hover:text-foret-600 hover:border-foret-200 transition-all cursor-pointer group/btn"
                                                        title="Inventaire du lot"
                                                    >
                                                        <i className="ri-archive-line text-xl transition-transform group-hover/btn:scale-110" />
                                                    </button>
                                                    <div className="flex flex-col gap-1.5 ml-2">
                                                        <button 
                                                            onClick={() => setEditingLot({...lot, date_reception: lot.date_achat})} 
                                                            className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-900 hover:text-white transition-all flex items-center justify-center text-xs group/edit"
                                                            title="Modifier le lot"
                                                        >
                                                            <i className="ri-edit-line" />
                                                        </button>
                                                        <button 
                                                            onClick={() => handleDeleteLot(lot.id)} 
                                                            className="w-8 h-8 rounded-lg bg-red-50 text-red-400 hover:bg-red-600 hover:text-white transition-all flex items-center justify-center text-xs group/del"
                                                            title="Supprimer le lot"
                                                        >
                                                            <i className="ri-delete-bin-line" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )
                        )}
                        
                        {activeTab === 'paiements' && (
                             <div className="space-y-4">
                                {selectedLotPayments.length === 0 ? (
                                    <div className="py-20 text-center opacity-40">
                                        <i className="ri-bank-card-line text-6xl mb-4 block" />
                                        <p className="font-bold uppercase tracking-widest">Aucune transaction enregistrée</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <thead>
                                                <tr className="text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-50">
                                                    <th className="pb-4">Date</th>
                                                    <th className="pb-4">Lot ID</th>
                                                    <th className="pb-4">Mode</th>
                                                    <th className="pb-4 text-right">Montant</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {selectedLotPayments.map(p => (
                                                    <tr key={p.id} className="text-sm">
                                                        <td className="py-4 font-bold text-gray-700">{formatDateTime(p.date_paiement)}</td>
                                                        <td className="py-4 font-medium text-gray-400">Lot #{p.lot_id}</td>
                                                        <td className="py-4">
                                                            <span className="bg-gray-100 px-2 py-1 rounded text-[10px] font-black uppercase">{p.mode_paiement}</span>
                                                        </td>
                                                        <td className="py-4 text-right font-black text-foret-600">{formatPrice(p.montant)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                             </div>
                        )}
                    </div>
                </div>
            </div>
          )}
        </main>
      </div>

      {/* Modals */}
      {showAddSupplier && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setShowAddSupplier(false)}>
            <div className="bg-white rounded-[3rem] w-full max-w-xl p-12 shadow-2xl animate-in fade-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-6 mb-10">
                    <div className="w-16 h-16 bg-foret-50 rounded-[1.5rem] flex items-center justify-center border border-foret-100 shadow-sm">
                        <i className="ri-building-line text-3xl text-foret-600" />
                    </div>
                    <div>
                        <h3 className="font-playfair font-black text-3xl text-gray-900 tracking-tight leading-none">Partenaire</h3>
                        <p className="text-gray-400 font-medium mt-2">Nouveau profil fournisseur</p>
                    </div>
                </div>
                
                <div className="space-y-6 mb-12">
                    <div>
                        <label className="block text-[11px] font-black text-gray-400 uppercase mb-2 ml-1 tracking-[1.5px]">Identification *</label>
                        <input 
                            type="text" 
                            value={newSupplier.nom}
                            onChange={e => setNewSupplier({...newSupplier, nom: e.target.value})}
                            className="w-full bg-gray-50 border-2 border-transparent rounded-2xl px-6 py-5 text-sm font-bold focus:outline-none focus:border-foret-400 focus:bg-white focus:shadow-lg transition-all" 
                            placeholder="Nom de l'entreprise ou contact" 
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-[11px] font-black text-gray-400 uppercase mb-2 ml-1 tracking-[1.5px]">Téléphone</label>
                            <input 
                                type="text"
                                value={newSupplier.telephone}
                                onChange={e => setNewSupplier({...newSupplier, telephone: e.target.value})}
                                className="w-full bg-gray-50 border-2 border-transparent rounded-2xl px-6 py-5 text-sm font-bold focus:outline-none focus:border-foret-400 focus:bg-white focus:shadow-lg transition-all" 
                                placeholder="+223 00 00 00 00" 
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] font-black text-gray-400 uppercase mb-2 ml-1 tracking-[1.5px]">Ville / Zone</label>
                            <input 
                                type="text" 
                                value={newSupplier.adresse}
                                onChange={e => setNewSupplier({...newSupplier, adresse: e.target.value})}
                                className="w-full bg-gray-50 border-2 border-transparent rounded-2xl px-6 py-5 text-sm font-bold focus:outline-none focus:border-foret-400 focus:bg-white focus:shadow-lg transition-all" 
                                placeholder="Bamako" 
                            />
                        </div>
                    </div>
                </div>

                <div className="flex gap-4">
                    <button onClick={() => setShowAddSupplier(false)} className="flex-1 py-5 bg-gray-100 text-gray-500 font-black rounded-2xl hover:bg-gray-200 transition-all cursor-pointer tracking-widest text-xs">ANNULER</button>
                    <button 
                        onClick={handleAddSupplier}
                        className="flex-1 py-5 bg-foret-600 text-white font-black rounded-2xl hover:bg-foret-700 shadow-2xl shadow-foret-200 transition-all cursor-pointer tracking-widest text-xs active:scale-95"
                    >
                        CRÉER LE PROFIL
                    </button>
                </div>
            </div>
        </div>
      )}

      {showAddLot && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setShowAddLot(false)}>
            <div className="bg-white rounded-[3rem] w-full max-w-xl p-12 shadow-2xl animate-in fade-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-6 mb-10">
                    <div className="w-16 h-16 bg-amber-50 rounded-[1.5rem] flex items-center justify-center border border-amber-100 shadow-sm">
                        <i className="ri-add-box-fill text-3xl text-amber-600" />
                    </div>
                    <div>
                        <h3 className="font-playfair font-black text-3xl text-gray-900 tracking-tight leading-none">Nouvel Achat</h3>
                        <p className="text-gray-400 font-medium mt-2">Enregistrement d'un nouveau lot</p>
                    </div>
                </div>
                
                <div className="space-y-6 mb-12">
                    <div className="col-span-2">
                        <label className="block text-[11px] font-black text-gray-400 uppercase mb-2 ml-1 tracking-[1.5px]">Nom du tissu (Principal)</label>
                        <input 
                            type="text" 
                            placeholder="Ex: Basin, Wax, Soie..."
                            className="w-full bg-gray-50 border-2 border-transparent rounded-2xl px-6 py-5 text-sm font-bold focus:outline-none focus:border-foret-400 focus:bg-white transition-all"
                            value={newLot.nom_tissu}
                            onChange={e => setNewLot({...newLot, nom_tissu: e.target.value})}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-[11px] font-black text-gray-400 uppercase mb-2 ml-1 tracking-[1.5px]">Date d'achat</label>
                            <input 
                                type="date" 
                                title="Sélectionner la date d'achat"
                                value={newLot.date_achat}
                                onChange={e => setNewLot({...newLot, date_achat: e.target.value})}
                                className="w-full bg-gray-50 border-2 border-transparent rounded-2xl px-6 py-5 text-sm font-bold focus:outline-none focus:border-foret-400 focus:bg-white transition-all" 
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] font-black text-gray-400 uppercase mb-2 ml-1 tracking-[1.5px]">Quantité (Mètres)</label>
                            <div className="relative">
                                <input 
                                    type="number" 
                                    value={newLot.quantite || ''}
                                    onChange={e => {
                                        const q = parseFloat(e.target.value) || 0;
                                        setNewLot({
                                            ...newLot, 
                                            quantite: q,
                                            montant_total: q * newLot.prix_unitaire
                                        });
                                    }}
                                    className="w-full bg-gray-50 border-2 border-transparent rounded-2xl px-6 py-5 text-sm font-black focus:outline-none focus:border-foret-400 focus:bg-white transition-all pr-12" 
                                    placeholder="0"
                                />
                                <span className="absolute right-5 top-1/2 -translate-y-1/2 font-black text-gray-300 text-xs uppercase">m</span>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-[11px] font-black text-gray-400 uppercase mb-2 ml-1 tracking-[1.5px]">Prix au Mètre (F)</label>
                            <div className="relative">
                                <input 
                                    type="number" 
                                    value={newLot.prix_unitaire || ''}
                                    onChange={e => {
                                        const p = parseFloat(e.target.value) || 0;
                                        setNewLot({
                                            ...newLot, 
                                            prix_unitaire: p,
                                            montant_total: newLot.quantite * p
                                        });
                                    }}
                                    className="w-full bg-gray-50 border-2 border-transparent rounded-2xl px-6 py-5 text-sm font-black focus:outline-none focus:border-foret-400 focus:bg-white transition-all" 
                                    placeholder="0"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-[11px] font-black text-gray-400 uppercase mb-2 ml-1 tracking-[1.5px]">Montant Total Direct</label>
                            <div className="relative">
                                <input 
                                    type="number" 
                                    title="Montant total payé directement au fournisseur"
                                    value={newLot.montant_total || ''}
                                    onChange={e => {
                                        const total = parseFloat(e.target.value) || 0;
                                        setNewLot({
                                            ...newLot, 
                                            montant_total: total,
                                            prix_unitaire: newLot.quantite > 0 ? total / newLot.quantite : 0
                                        });
                                    }}
                                    className="w-full bg-gray-50 border-2 border-transparent rounded-2xl px-6 py-5 text-sm font-black focus:outline-none focus:border-foret-400 focus:bg-white transition-all pr-12" 
                                    placeholder="0"
                                />
                                <span className="absolute right-5 top-1/2 -translate-y-1/2 font-black text-gray-300 text-xs uppercase">F</span>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="block text-[11px] font-black text-gray-400 uppercase mb-2 ml-1 tracking-[1.5px]">Notes / Commentaires</label>
                        <textarea 
                            value={newLot.notes}
                            onChange={e => setNewLot({...newLot, notes: e.target.value})}
                            className="w-full bg-gray-50 border-2 border-transparent rounded-2xl px-6 py-4 text-sm font-medium focus:outline-none focus:border-foret-400 focus:bg-white transition-all resize-none h-32" 
                            placeholder="Détails du lot (Ex: Couleur, qualité...)"
                        />
                    </div>
                </div>

                <div className="flex gap-4">
                    <button onClick={() => setShowAddLot(false)} className="flex-1 py-5 bg-gray-100 text-gray-500 font-black rounded-2xl hover:bg-gray-200 transition-all cursor-pointer tracking-widest text-xs">FERMER</button>
                    <button 
                        onClick={handleAddLot}
                        className="flex-1 py-5 bg-gray-900 text-white font-black rounded-2xl hover:bg-black shadow-2xl shadow-gray-200 transition-all cursor-pointer tracking-widest text-xs active:scale-95"
                    >
                        VALIDER L'ACHAT
                    </button>
                </div>
            </div>
        </div>
      )}

      {showAddPayment && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setShowAddPayment(null)}>
            <div className="bg-white rounded-[3rem] w-full max-w-sm p-10 shadow-2xl animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
                <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-foret-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-foret-200 border-8 border-foret-50">
                        <i className="ri-hand-coin-line text-3xl text-white" />
                    </div>
                    <h3 className="font-playfair font-black text-2xl text-gray-900 leading-none">Règlement Lot #{showAddPayment.id}</h3>
                    <p className="text-gray-400 text-xs font-bold uppercase mt-2 tracking-widest">Enregistrement partiel ou total</p>
                </div>
                
                <div className="space-y-5 mb-10">
                    <div>
                        <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1 tracking-[1.5px]">Somme à verser</label>
                        <div className="relative">
                            <input 
                                type="number" 
                                value={newPayment.montant}
                                onChange={e => setNewPayment({...newPayment, montant: parseFloat(e.target.value)})}
                                className="w-full bg-gray-50 border-2 border-transparent rounded-[1.25rem] px-6 py-4 text-lg font-black focus:outline-none focus:border-foret-400 focus:bg-white transition-all text-center pr-12" 
                                placeholder="0"
                                max={showAddPayment.montant_total - showAddPayment.montant_paye}
                            />
                            <span className="absolute right-6 top-1/2 -translate-y-1/2 font-black text-gray-900">F</span>
                        </div>
                    </div>
                        <div className="flex items-center justify-between mb-1.5 ml-1">
                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[1.5px]">Mode de paiement</label>
                            <button 
                                onClick={() => setShowManagePaymentModes(true)}
                                className="text-[9px] font-black text-foret-600 hover:text-foret-700 uppercase tracking-tighter"
                            >
                                Gérer les modes
                            </button>
                        </div>
                        <div className="relative">
                            <select 
                                value={newPayment.mode_paiement}
                                title="Sélectionner le mode de paiement"
                                onChange={e => setNewPayment({...newPayment, mode_paiement: e.target.value})}
                                className="w-full bg-white border-2 border-slate-200 rounded-[1.25rem] px-6 py-4 text-sm font-bold text-slate-900 focus:outline-none focus:border-foret-400 appearance-none cursor-pointer pr-12 transition-all shadow-sm"
                            >
                                {paymentModes.length === 0 ? (
                                    <>
                                        <option value="Espèces" className="text-slate-900">Espèces</option>
                                        <option value="Orange Money" className="text-slate-900">Orange Money</option>
                                        <option value="Moov Money" className="text-slate-900">Moov Money</option>
                                        <option value="Virement Bancaire" className="text-slate-900">Virement Bancaire</option>
                                    </>
                                ) : (
                                    paymentModes.map((m: any) => (
                                        <option key={m.id} value={m.label} className="text-slate-900">{m.label}</option>
                                    ))
                                )}
                            </select>
                            <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                <i className="ri-arrow-down-s-line text-xl" />
                            </div>
                        </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <button onClick={() => setShowAddPayment(null)} className="py-4 bg-gray-100 text-gray-500 font-black rounded-2xl hover:bg-gray-200 transition-all cursor-pointer text-[10px] tracking-widest uppercase">RETOUR</button>
                    <button 
                        onClick={handleAddPayment}
                        className="py-4 bg-foret-600 text-white font-black rounded-2xl hover:bg-foret-700 shadow-xl shadow-foret-200 transition-all cursor-pointer text-[10px] tracking-widest uppercase active:scale-95"
                    >
                        CONFIRMER
                    </button>
                </div>
            </div>
        </div>
      )}

      {showLotArticles && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setShowLotArticles(null)}>
            <div className="bg-white rounded-[3rem] w-full max-w-2xl p-12 shadow-2xl flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-10 shrink-0">
                    <div className="flex items-center gap-6">
                        <div className="w-16 h-16 bg-gray-900 rounded-[1.5rem] flex items-center justify-center shadow-lg">
                            <i className="ri-archive-line text-3xl text-white" />
                        </div>
                        <div>
                            <h3 className="font-playfair font-black text-3xl text-gray-900 tracking-tight leading-none">Inventaire du Lot</h3>
                            <p className="text-gray-400 font-black text-[10px] uppercase tracking-widest mt-2">{selectedLotArticles.length} ARTICLES RÉPERTORIÉS</p>
                        </div>
                    </div>
                    <button onClick={() => setShowLotArticles(null)} title="Fermer" className="w-14 h-14 rounded-2xl hover:bg-gray-100 text-gray-400 transition-all active:scale-90 cursor-pointer flex items-center justify-center">
                        <i className="ri-close-line text-3xl" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto space-y-4 pr-2 scrollbar-thin scrollbar-thumb-gray-200">
                    {selectedLotArticles.length === 0 ? (
                        <div className="py-20 text-center opacity-30">
                            <p className="font-black text-xs uppercase tracking-widest">Liste d'articles vide</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="text-left border-b border-gray-100">
                                        <th className="pb-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Article</th>
                                        <th className="pb-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Initial</th>
                                        <th className="pb-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Reste</th>
                                        <th className="pb-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {selectedLotArticles.map((art: any) => (
                                        <tr key={art.id} className="group">
                                            <td className="py-4">
                                                <p className="font-black text-gray-900 uppercase text-xs">{art.designation}</p>
                                                <p className="text-[10px] text-gray-400 font-bold">{formatPrice(art.prix_unitaire)} / unité</p>
                                            </td>
                                            <td className="py-4 text-center font-bold text-gray-400 text-xs">{art.quantite} m</td>
                                            <td className="py-4 text-center">
                                                <span className={`px-3 py-1 rounded-full text-[10px] font-black ${art.quantite_restante <= (art.seuil_alerte || 5) ? 'bg-red-50 text-red-600 animate-pulse border border-red-100' : 'bg-foret-50 text-foret-600 border border-foret-50'}`}>
                                                    {art.quantite_restante} m
                                                </span>
                                            </td>
                                            <td className="py-4 text-right">
                                                <button 
                                                    onClick={() => {
                                                        const val = prompt(`Combien de mètres utilisés pour ${art.designation} ?`);
                                                        if (val && !isNaN(parseFloat(val))) handleUpdateStock(art.id, parseFloat(val));
                                                    }}
                                                    className="bg-gray-100 hover:bg-gray-900 hover:text-white px-3 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all cursor-pointer"
                                                >
                                                    Utiliser
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="mt-8 pt-8 border-t border-gray-100 flex items-center justify-between shrink-0">
                    <div>
                        <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">Valeur Totale du Lot</p>
                        <p className="text-xl font-black text-gray-900">{formatPrice(selectedLotArticles.reduce((acc: number, a: any) => acc + (a.quantite * a.prix_unitaire), 0))}</p>
                    </div>
                    <button 
                        onClick={() => setShowAddArticle(true)}
                        className="px-8 py-4 bg-gray-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all shadow-xl active:scale-95 cursor-pointer"
                    >
                        COMMANDER PLUS
                    </button>
                </div>
            </div>
        </div>
      )}

      {showAddArticle && (
        <div className="fixed inset-0 bg-black/40 z-[110] flex items-center justify-center p-4">
            <div className="bg-white rounded-[2.5rem] p-10 w-full max-w-md shadow-2xl animate-in zoom-in-95 duration-300">
                <h3 className="font-playfair font-black text-2xl text-gray-900 mb-8 tracking-tight">Nouvel Article (Stock)</h3>
                <div className="space-y-5 mb-8">
                    <div>
                        <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Désignation du tissu</label>
                        <input 
                            type="text"
                            className="w-full bg-gray-50 border-2 border-transparent rounded-2xl px-6 py-4 text-sm font-bold focus:border-gray-900 outline-none transition-all"
                            value={newArticle.designation}
                            onChange={e => setNewArticle({...newArticle, designation: e.target.value})}
                            placeholder="Ex: Basin Riche..."
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Quantité (m)</label>
                            <input 
                                type="number"
                                className="w-full bg-gray-50 border-2 border-transparent rounded-2xl px-6 py-4 text-sm font-bold focus:border-gray-900 outline-none transition-all"
                                value={newArticle.quantite || ''}
                                onChange={e => setNewArticle({...newArticle, quantite: parseFloat(e.target.value)})}
                                placeholder="0.00"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Seuil Alerte (m)</label>
                            <input 
                                type="number"
                                className="w-full bg-gray-50 border-2 border-transparent rounded-2xl px-6 py-4 text-sm font-bold focus:border-gray-900 outline-none transition-all"
                                value={newArticle.seuil_alerte || ''}
                                onChange={e => setNewArticle({...newArticle, seuil_alerte: parseFloat(e.target.value) || 5})}
                                placeholder="5.00"
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Prix Unit (F)</label>
                            <input 
                                type="number"
                                className="w-full bg-gray-50 border-2 border-transparent rounded-2xl px-6 py-4 text-sm font-bold focus:border-gray-900 outline-none transition-all"
                                value={newArticle.prix_unitaire || ''}
                                onChange={e => setNewArticle({...newArticle, prix_unitaire: parseFloat(e.target.value)})}
                                placeholder="0"
                            />
                        </div>
                    </div>
                </div>
                <div className="flex gap-3">
                    <button onClick={() => setShowAddArticle(false)} className="flex-1 py-4 bg-gray-100 text-gray-600 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-gray-200 transition-all cursor-pointer">Annuler</button>
                    <button onClick={handleAddArticle} className="flex-1 py-4 bg-gray-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-black transition-all cursor-pointer">Confirmer</button>
                </div>
            </div>
        </div>
      )}
      {showInvoice && invoiceLot && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[120] flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-[3rem] w-full max-w-4xl p-12 shadow-2xl relative my-10 animate-in zoom-in-95 duration-300">
                <div className="flex items-center justify-between mb-12 shrink-0 border-b border-gray-100 pb-8">
                    <div className="flex items-center gap-6">
                        <div className="w-20 h-20 bg-gray-900 rounded-[2rem] flex items-center justify-center shadow-2xl">
                            <i className="ri-file-text-line text-4xl text-white" />
                        </div>
                        <div>
                            <h3 className="font-playfair font-black text-4xl text-gray-900 tracking-tight leading-none">Note de Règlement</h3>
                            <p className="text-gray-400 font-black text-xs uppercase tracking-widest mt-2">Document officiel — Atelier Bassoum</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex bg-gray-100 rounded-2xl p-1.5">
                            <button 
                                onClick={() => setInvoiceMode('lot')} 
                                className={`px-5 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${invoiceMode === 'lot' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                            >Lot Unique</button>
                            <button 
                                onClick={() => setInvoiceMode('global')} 
                                className={`px-5 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${invoiceMode === 'global' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                            >Relevé Global</button>
                        </div>
                        <button onClick={() => setShowInvoice(false)} title="Fermer" className="w-14 h-14 rounded-2xl hover:bg-gray-100 text-gray-400 transition-all flex items-center justify-center">
                            <i className="ri-close-line text-3xl" />
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-20 mb-16">
                    <div>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Émetteur</p>
                        <p className="font-black text-xl text-gray-900 uppercase">Atelier Bassoum</p>
                        <p className="text-sm text-gray-500 font-medium leading-relaxed mt-2">Service de Gestion des Stocks<br/>Bamako, Mali</p>
                    </div>
                    <div className="text-right">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Destinataire (Fournisseur)</p>
                        <p className="font-black text-xl text-gray-900 uppercase">{selectedSupplier?.nom}</p>
                        <p className="text-sm text-gray-500 font-medium leading-relaxed mt-2">{selectedSupplier?.telephone}<br/>{selectedSupplier?.adresse || 'Bamako'}</p>
                    </div>
                </div>

                <div className="bg-gray-50 rounded-[2.5rem] p-10 mb-12 border border-gray-100 relative overflow-hidden">
                    <i className="ri-quote-fill absolute -top-4 -right-4 text-9xl text-gray-200/50" />
                    <div className="relative z-10">
                        <h4 className="font-black text-sm uppercase tracking-widest text-gray-400 mb-8 border-b border-gray-200 pb-4">Détails de la transaction</h4>
                        
                        {invoiceMode === 'lot' ? (
                            <div className="space-y-10">
                                <div className="grid grid-cols-3 gap-10">
                                    <div>
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Référence Lot</p>
                                        <p className="font-black text-2xl text-gray-900">#{invoiceLot.id}</p>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase mt-1 italic">{invoiceLot.nom_tissu || 'Divers tissus'}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Date Réception</p>
                                        <p className="font-black text-lg text-gray-900 uppercase">{new Date(invoiceLot.date_achat).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Statut</p>
                                        <span className={`px-4 py-1.5 rounded-full text-[10px] font-black border ${invoiceLot.montant_total - (invoiceLot.montant_paye || 0) === 0 ? 'bg-foret-500 text-white border-foret-500' : 'bg-red-50 text-red-600 border-red-100'}`}>
                                            {invoiceLot.montant_total - (invoiceLot.montant_paye || 0) === 0 ? 'SOLDÉ' : 'ENCOURS'}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-3 gap-10 pt-8 border-t border-gray-200/50">
                                    <div>
                                        <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Montant du Lot</p>
                                        <p className="font-black text-2xl text-gray-900">{formatPrice(invoiceLot.montant_total)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Déjà Payé</p>
                                        <p className="font-black text-2xl text-foret-600">{formatPrice(invoiceLot.montant_paye || 0)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black text-red-400 uppercase tracking-widest mb-2">Reste à Régler</p>
                                        <p className="font-black text-2xl text-red-600">{formatPrice(invoiceLot.montant_total - (invoiceLot.montant_paye || 0))}</p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-3 gap-10">
                                <div>
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Total Achats</p>
                                    <p className="font-black text-2xl text-gray-900">{formatPrice(selectedSupplier?.total_du || 0)}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Cumul Payé</p>
                                    <p className="font-black text-2xl text-foret-600">{formatPrice(selectedSupplier?.total_paye || 0)}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-black text-red-400 uppercase tracking-widest mb-2">Balance Dûe</p>
                                    <p className="font-black text-2xl text-red-600">{formatPrice((selectedSupplier?.total_du || 0) - (selectedSupplier?.total_paye || 0))}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="mb-16">
                    <h4 className="font-black text-[10px] uppercase tracking-widest text-gray-400 mb-6 flex items-center gap-3">
                        Historique des règlements
                        <span className="h-px bg-gray-100 flex-1"></span>
                    </h4>
                    <div className="space-y-4">
                        {(invoiceMode === 'lot' ? selectedLotPayments.filter(p => p.lot_id === invoiceLot.id) : selectedLotPayments).length === 0 ? (
                            <p className="text-center py-10 text-xs font-bold text-gray-400 uppercase tracking-widest">Aucun paiement enregistré</p>
                        ) : (
                            (invoiceMode === 'lot' ? selectedLotPayments.filter(p => p.lot_id === invoiceLot.id) : selectedLotPayments).map(p => (
                                <div key={p.id} className="flex items-center justify-between p-7 bg-white border border-gray-100 rounded-[1.5rem] hover:shadow-lg transition-all group">
                                    <div className="flex items-center gap-6">
                                        <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-100 group-hover:bg-foret-50 group-hover:border-foret-100 group-hover:text-foret-600 text-gray-400 transition-all">
                                            <i className="ri-checkbox-circle-fill text-xl" />
                                        </div>
                                        <div>
                                            <p className="font-black text-gray-900 text-sm">{formatDateTime(p.date_paiement)}</p>
                                            <p className="text-[10px] text-gray-400 font-bold uppercase mt-1 tracking-wider">{p.mode_paiement} {invoiceMode === 'global' && `— Lot #${p.lot_id}`}</p>
                                        </div>
                                    </div>
                                    <p className="font-black text-foret-600 text-lg">+{formatPrice(p.montant)}</p>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="flex gap-4 pt-8 border-t border-gray-100">
                    <button 
                      onClick={() => setShowInvoice(false)}
                      className="flex-1 py-6 bg-gray-100 text-gray-500 font-black rounded-[1.5rem] tracking-widest text-[10px] uppercase hover:bg-gray-200 transition-all cursor-pointer"
                    >FERMER LE DOCUMENT</button>
                    <button 
                      onClick={() => {
                        const summary = invoiceMode === 'lot' 
                            ? `FACTURE LOT #${invoiceLot.id} - ${invoiceLot.nom_tissu || ''}\nTotal: ${formatPrice(invoiceLot.montant_total)}\nRestant: ${formatPrice(invoiceLot.montant_total - (invoiceLot.montant_paye || 0))}`
                            : `RELEVÉ GLOBAL - ${selectedSupplier?.nom}\nTotal Achats: ${formatPrice(selectedSupplier?.total_du || 0)}\nTotal Payé: ${formatPrice(selectedSupplier?.total_paye || 0)}\nSolde Dû: ${formatPrice((selectedSupplier?.total_du || 0) - (selectedSupplier?.total_paye || 0))}`;
                        
                        window.open(`https://wa.me/${selectedSupplier?.telephone.replace(/\s+/g, '')}?text=${encodeURIComponent("*DOCUMENT DE PAIEMENT*\n\n" + summary + "\n\nMerci pour votre professionnalisme.")}`, '_blank');
                      }}
                      className="flex-[2] py-6 bg-emerald-600 text-white font-black rounded-[1.5rem] tracking-widest text-[10px] uppercase hover:bg-emerald-700 shadow-xl transition-all flex items-center justify-center gap-3 cursor-pointer active:scale-95"
                    >
                      <i className="ri-whatsapp-line text-lg" />
                      PARTAGER SUR WHATSAPP
                    </button>
                    <button 
                      onClick={() => window.print()}
                      className="w-20 bg-gray-900 text-white rounded-[1.5rem] flex items-center justify-center hover:bg-black transition-all shadow-xl active:scale-95 cursor-pointer"
                      title="Imprimer / PDF"
                    >
                      <i className="ri-printer-line text-2xl" />
                    </button>
                </div>
            </div>
        </div>
      )}

      {editingLot && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[120] flex items-center justify-center p-4">
            <div className="bg-white rounded-[3rem] w-full max-w-xl p-12 shadow-2xl animate-in zoom-in-95 duration-300">
                <div className="flex items-center gap-6 mb-10">
                    <div className="w-16 h-16 bg-amber-50 rounded-[1.5rem] flex items-center justify-center border border-amber-100 shadow-sm">
                        <i className="ri-edit-box-fill text-3xl text-amber-600" />
                    </div>
                    <div>
                        <h3 className="font-playfair font-black text-3xl text-gray-900 tracking-tight leading-none">Modifier le Lot #{editingLot.id}</h3>
                        <p className="text-gray-400 font-medium mt-2">Mise à jour des informations d'achat</p>
                    </div>
                </div>
                
                <div className="space-y-6 mb-12">
                    <div className="col-span-2">
                        <label htmlFor="edit-libelle" className="block text-[11px] font-black text-gray-400 uppercase mb-2 ml-1 tracking-[1.5px]">Libellé / Désignation</label>
                        <input 
                            id="edit-libelle"
                            type="text" 
                            placeholder="Libellé du lot"
                            className="w-full bg-gray-50 border-2 border-transparent rounded-2xl px-6 py-5 text-sm font-bold focus:outline-none focus:border-foret-400 focus:bg-white transition-all font-black uppercase"
                            value={editingLot.libelle || ''}
                            onChange={e => setEditingLot({...editingLot, libelle: e.target.value})}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="edit-date" className="block text-[11px] font-black text-gray-400 uppercase mb-2 ml-1 tracking-[1.5px]">Date d'achat</label>
                            <input 
                                id="edit-date"
                                type="date" 
                                placeholder="Date"
                                value={editingLot.date_reception || ''}
                                onChange={e => setEditingLot({...editingLot, date_reception: e.target.value})}
                                className="w-full bg-gray-50 border-2 border-transparent rounded-2xl px-6 py-5 text-sm font-bold focus:outline-none focus:border-foret-400 focus:bg-white transition-all font-black" 
                            />
                        </div>
                        <div>
                            <label htmlFor="edit-montant" className="block text-[11px] font-black text-gray-400 uppercase mb-2 ml-1 tracking-[1.5px]">Montant de base (F)</label>
                            <input 
                                id="edit-montant"
                                type="number" 
                                placeholder="0"
                                value={editingLot.montant_total_direct || ''}
                                onChange={e => setEditingLot({...editingLot, montant_total_direct: parseFloat(e.target.value)})}
                                className="w-full bg-gray-50 border-2 border-transparent rounded-2xl px-6 py-5 text-sm font-black focus:outline-none focus:border-foret-400 focus:bg-white transition-all font-black" 
                            />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="edit-notes" className="block text-[11px] font-black text-gray-400 uppercase mb-2 ml-1 tracking-[1.5px]">Notes / Commentaires</label>
                        <textarea 
                            id="edit-notes"
                            placeholder="Commentaires"
                            value={editingLot.notes_lot || ''}
                            onChange={e => setEditingLot({...editingLot, notes_lot: e.target.value})}
                            className="w-full bg-gray-50 border-2 border-transparent rounded-2xl px-6 py-4 text-sm font-medium focus:outline-none focus:border-foret-400 focus:bg-white transition-all resize-none h-32" 
                        />
                    </div>
                </div>

                <div className="flex gap-4">
                    <button onClick={() => setEditingLot(null)} className="flex-1 py-5 bg-gray-100 text-gray-500 font-black rounded-2xl hover:bg-gray-200 transition-all cursor-pointer tracking-widest text-xs">ANNULER</button>
                    <button 
                        onClick={handleUpdateLot}
                        className="flex-1 py-5 bg-gray-900 text-white font-black rounded-2xl hover:bg-black shadow-2xl shadow-gray-200 transition-all cursor-pointer tracking-widest text-xs active:scale-95"
                    >
                        METTRE À JOUR
                    </button>
                </div>
            </div>
        </div>
      )}
      {showManagePaymentModes && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[150] flex items-center justify-center p-4">
            <div className="bg-white rounded-[3rem] w-full max-w-md p-10 shadow-2xl animate-in zoom-in-95 duration-300">
                <div className="flex items-center justify-between mb-8">
                    <h3 className="font-playfair font-black text-2xl text-gray-900 tracking-tight">Modes de Paiement</h3>
                    <button onClick={() => setShowManagePaymentModes(false)} title="Fermer" className="text-gray-400 hover:text-gray-600">
                        <i className="ri-close-line text-2xl" />
                    </button>
                </div>

                <div className="space-y-4 mb-8 max-h-60 overflow-y-auto pr-2">
                    {paymentModes.map((m: any) => (
                        <div key={m.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100 group">
                            <span className="font-black text-xs uppercase text-gray-900">{m.label}</span>
                            <button 
                                onClick={() => handleDeletePaymentMode(m.id)}
                                title="Supprimer"
                                className="w-8 h-8 rounded-lg bg-red-50 text-red-400 hover:bg-red-600 hover:text-white transition-all flex items-center justify-center opacity-0 group-hover:opacity-100"
                            >
                                <i className="ri-delete-bin-line" />
                            </button>
                        </div>
                    ))}
                    {paymentModes.length === 0 && (
                        <p className="text-center py-4 text-gray-400 text-[10px] font-bold uppercase tracking-widest italic">Aucun mode personnalisé</p>
                    )}
                </div>

                <div className="pt-6 border-t border-gray-100">
                    <label className="block text-[10px] font-black text-gray-400 uppercase mb-3 tracking-widest pl-1">Nouveau mode</label>
                    <div className="flex gap-2">
                        <input 
                            type="text"
                            className="flex-1 bg-gray-50 border-2 border-transparent rounded-xl px-4 py-3 text-xs font-black uppercase focus:border-foret-400 focus:bg-white outline-none transition-all"
                            placeholder="Ex: WAVE, Chèque..."
                            value={newPaymentModeLabel}
                            onChange={e => setNewPaymentModeLabel(e.target.value)}
                        />
                        <button 
                            onClick={handleCreatePaymentMode}
                            className="bg-gray-900 text-white px-5 rounded-xl text-xs font-black hover:bg-black transition-all active:scale-95"
                        >
                            AJOUTER
                        </button>
                    </div>
                </div>
            </div>
        </div>
      )}
      <FloatingActionButton 
        onClick={view === 'list' ? () => setShowAddSupplier(true) : () => setShowAddLot(true)} 
        icon={view === 'list' ? "ri-user-add-line" : "ri-add-box-line"} 
        label={view === 'list' ? "Ajouter un fournisseur" : "Nouvel arrivage"} 
      />
    </div>
  );
}
